import {
    Keyboard,
    ArrowUp,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
} from 'lucide-react';
import React from 'react';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Kbd } from '@/components/ui/kbd';
import { Switch } from '@/components/ui/switch';

import { useTradingStore } from '../store';

interface ShortcutsModalProps {
    onClose: () => void;
}

const ShortcutRow: React.FC<{
    keys: React.ReactNode[];
    label: string;
    description?: string;
}> = ({ keys, label, description }) => (
    <div className="flex items-center justify-between border-b border-border py-3 last:border-0">
        <div className="flex flex-col">
            <span className="text-sm font-bold text-foreground">{label}</span>
            {description && (
                <span className="text-[10px] text-muted-foreground">
                    {description}
                </span>
            )}
        </div>
        <div className="flex items-center gap-2">
            {keys.map((k, i) => (
                <React.Fragment key={i}>
                    {i > 0 && (
                        <span className="text-xs text-muted-foreground">
                            or
                        </span>
                    )}
                    <Kbd>{k}</Kbd>
                </React.Fragment>
            ))}
        </div>
    </div>
);

const ShortcutsModal: React.FC<ShortcutsModalProps> = ({ onClose }) => {
    const shortcutsEnabled = useTradingStore((s) => s.shortcutsEnabled);
    const setShortcutsEnabled = useTradingStore((s) => s.setShortcutsEnabled);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-sm gap-0 overflow-hidden p-0">
                <DialogHeader className="border-b border-border p-4">
                    <DialogTitle className="flex items-center gap-2 text-muted-foreground">
                        <Keyboard size={18} />
                        <span className="text-sm font-semibold uppercase tracking-[0.08em]">
                            Keyboard controls
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <label className="flex cursor-pointer items-center justify-between border-b border-border px-4 py-3">
                    <span className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground">
                            Enable shortcuts
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                            Trade keys below, ⌘K always works
                        </span>
                    </span>
                    <Switch
                        checked={shortcutsEnabled}
                        onCheckedChange={setShortcutsEnabled}
                    />
                </label>

                <div
                    className={
                        shortcutsEnabled
                            ? 'p-4'
                            : 'pointer-events-none p-4 opacity-75'
                    }
                >
                    <ShortcutRow
                        label="Open long"
                        keys={['L', <ArrowUp size={14} aria-label="Up arrow" />]}
                    />
                    <ShortcutRow
                        label="Open short"
                        keys={[
                            'S',
                            <ArrowDown size={14} aria-label="Down arrow" />,
                        ]}
                    />
                    <ShortcutRow
                        label="Increase bet"
                        keys={[
                            '+',
                            <ArrowRight size={14} aria-label="Right arrow" />,
                        ]}
                    />
                    <ShortcutRow
                        label="Decrease bet"
                        keys={[
                            '-',
                            <ArrowLeft size={14} aria-label="Left arrow" />,
                        ]}
                    />
                </div>

                <DialogFooter className="border-t border-border bg-muted/50 p-3">
                    <span className="w-full text-center font-sans text-[10px] text-muted-foreground">
                        {shortcutsEnabled
                            ? 'Press keys to execute actions instantly'
                            : 'Shortcuts are off (clicks only)'}
                    </span>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShortcutsModal;
